import { useSentry } from '@/hooks/useSentry';
import { Breadcrumbs, Link, Typography, useTheme } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import { BreadcrumbItem } from '@/types/common';

export interface SVBreadcrumbProps {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  maxItems?: number;
  itemsBeforeCollapse?: number;
  itemsAfterCollapse?: number;
  breadcrumbLabel?: string;
  sx?: any;
}

export default function SVBreadcrumb({
  items,
  separator = <NavigateNext fontSize="small" />,
  maxItems,
  itemsBeforeCollapse = 1,
  itemsAfterCollapse = 1,
  breadcrumbLabel,
  sx,
}: SVBreadcrumbProps) {
  const theme = useTheme();
  const { addUserActionBreadcrumb } = useSentry();

  const handleItemClick = (item: BreadcrumbItem) => {
    if (breadcrumbLabel) {
      addUserActionBreadcrumb(`Clicked breadcrumb: ${item.label}`, 'ui.click');
    }
    item.onClick?.();
  };

  return (
    <Breadcrumbs
      separator={separator}
      maxItems={maxItems}
      itemsBeforeCollapse={itemsBeforeCollapse}
      itemsAfterCollapse={itemsAfterCollapse}
      sx={{
        '& .MuiBreadcrumbs-separator': {
          color: theme.palette.text.secondary,
        },
        ...sx,
      }}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isActive = item.isActive !== undefined ? item.isActive : isLast;

        if (isActive) {
          return (
            <Typography
              key={index}
              color="text.primary"
              variant="body1"
              component="h6">
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={index}
            color="inherit"
            href={item.href}
            onClick={e => {
              if (item.onClick) {
                e.preventDefault();
                handleItemClick(item);
              }
            }}
            sx={{
              textDecoration: 'none',
              variant: 'h6',
              fontWeight: 900,
              color: 'primary.main',
              '&:hover': {
                color: theme.palette.primary.dark,
              },
            }}>
            {item.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}
